import { useState, useEffect, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import agentsService from "../services/agentsService";

export const useCreateAgent = (agentId = null, setSearchParams = null) => {
  const [currentTab, setCurrentTab] = useState(1);
  const [createdAgent, setCreatedAgent] = useState(null);
  const [agentData, setAgentData] = useState({
    name: "",
    description: "",
    model_provider: "",
    model_name: "",
    behavior_rules: "",
    escalation_conditions: "",
    hands_off_type: false,
  });

  const isEditMode = !!agentId;

  // Load existing agent data for edit mode
  const {
    data: existingAgent,
    isLoading: isLoadingAgent,
    error: loadAgentError,
  } = useQuery({
    queryKey: ["agent", agentId],
    queryFn: () => agentsService.getAgent(agentId),
    enabled: isEditMode,
  });

  // Update agentData when existing agent data is loaded
  useEffect(() => {
    if (isEditMode && existingAgent?.agent) {
      const agent = existingAgent.agent;
      setCreatedAgent(agent);
      setAgentData({
        name: agent.name || "",
        description: agent.description || "",
        model_provider: agent.model_provider || "",
        model_name: agent.model_name || "",
        behavior_rules: agent.behavior_rules || "",
        escalation_conditions: agent.escalation_conditions || "",
        hands_off_type: agent.hands_off_type || false,
      });
    }
  }, [isEditMode, existingAgent]);

  // Create agent mutation
  const createAgentMutation = useMutation({
    mutationFn: (data) => {
      // Add hardcoded context_window_length
      const agentPayload = {
        ...data,
        context_window_length: 20,
      };
      return agentsService.createAgent(agentPayload);
    },
    onSuccess: (data) => {
      console.log("✅ Create Agent Success:", data);
      const agent = data.agent || data; // Handle both structures
      setCreatedAgent(agent);
      setCurrentTab(2); // Unlock knowledge tab

      // Update URL with new agent ID if setSearchParams is provided
      if (setSearchParams && agent?.id) {
        console.log("🔄 Updating URL params:", { tab: "2", id: agent.id });
        setSearchParams(
          { tab: "2", id: agent.id.toString() },
          { replace: true }
        );
        // Ensure tab state is synced after URL update
        setTimeout(() => {
          console.log("🔄 Syncing tab state after URL update");
          setCurrentTab(2);
        }, 50);
      } else {
        console.warn("⚠️ No setSearchParams or agent.id:", {
          setSearchParams: !!setSearchParams,
          agentId: agent?.id,
        });
      }
    },
  });

  // Update agent mutation
  const updateAgentMutation = useMutation({
    mutationFn: (data) => {
      const agentPayload = {
        ...data,
        context_window_length: 20,
      };
      return agentsService.updateAgent(agentId, agentPayload);
    },
    onSuccess: (data) => {
      console.log("✅ Update Agent Success:", data);
      const agent = data.agent || data; // Handle both structures
      setCreatedAgent(agent);
      // Stay on current tab for edit mode
    },
  });

  // Delete agent mutation
  const deleteAgentMutation = useMutation({
    mutationFn: (id) => agentsService.deleteAgent(id),
    onSuccess: () => {
      console.log("✅ Delete Agent Success");
      // Reset form and navigate back to agents list
      resetForm();
      if (setSearchParams) {
        // Clear search params and navigate to agents page
        window.location.href = "/admin/agents";
      }
    },
  });

  // Tab access control
  const canAccessTab = useCallback(
    (tabNumber) => {
      switch (tabNumber) {
        case 1:
          return true; // Always accessible
        case 2:
          return !!createdAgent || isEditMode || !!agentId; // Accessible after agent created, edit mode, or if agentId exists
        case 3:
          return !!createdAgent || isEditMode || !!agentId; // Accessible if agent exists, edit mode, or if agentId exists
        default:
          return false;
      }
    },
    [createdAgent, isEditMode, agentId]
  );

  const updateAgentData = useCallback((newData) => {
    setAgentData((prev) => ({ ...prev, ...newData }));
  }, []);

  const resetForm = useCallback(() => {
    setCurrentTab(1);
    setCreatedAgent(null);
    setAgentData({
      name: "",
      description: "",
      model_provider: "",
      model_name: "",
      behavior_rules: "",
      escalation_conditions: "",
      hands_off_type: false,
    });
  }, []);

  // Determine which mutation to use
  const saveAgent = isEditMode
    ? updateAgentMutation.mutateAsync
    : createAgentMutation.mutateAsync;
  const isSavingAgent = isEditMode
    ? updateAgentMutation.isPending
    : createAgentMutation.isPending;
  const saveAgentError = isEditMode
    ? updateAgentMutation.error
    : createAgentMutation.error;

  return {
    currentTab,
    setCurrentTab,
    createdAgent,
    agentData,
    updateAgentData,
    createAgent: createAgentMutation.mutateAsync,
    updateAgent: updateAgentMutation.mutateAsync,
    deleteAgent: deleteAgentMutation.mutateAsync,
    saveAgent,
    isCreatingAgent: createAgentMutation.isPending,
    isUpdatingAgent: updateAgentMutation.isPending,
    isDeletingAgent: deleteAgentMutation.isPending,
    isSavingAgent,
    createAgentError: createAgentMutation.error,
    updateAgentError: updateAgentMutation.error,
    deleteAgentError: deleteAgentMutation.error,
    saveAgentError,
    isLoadingAgent,
    loadAgentError,
    isEditMode,
    canAccessTab,
    resetForm,
  };
};
